import * as yup from "yup";

/**
 * Reusable validation test functions for common field types
 * These ensure consistent validation across the entire application
 */

// ==================== TEXT FIELD VALIDATIONS ====================

/**
 * Validates that a text field doesn't contain only spaces
 * @param {boolean} isRequired - Whether the field is required
 * @returns {function} Yup test function
 */
export const noOnlySpacesTest = (isRequired = false) => ({
  name: 'no-only-spaces',
  message: 'This field cannot contain only spaces',
  test: function(value) {
    if (!value || value === '') return !isRequired;
    return value.trim().length > 0;
  }
});

/**
 * Validates that a text field doesn't contain special characters
 * Allows only letters (a-z, A-Z) and spaces
 * @returns {function} Yup test function
 */
export const noSpecialCharactersTest = () => ({
  name: 'no-special-characters',
  message: 'This field cannot contain special characters',
  test: function(value) {
    if (!value || value === '') return true;
    return /^[a-zA-Z\s]+$/.test(value);
  }
});

/**
 * Validates that a name field contains at least one letter
 * @returns {function} Yup test function
 */
export const containsLetterTest = () => ({
  name: 'contains-letter',
  message: 'This field must contain at least one letter',
  test: function(value) {
    if (!value || value === '') return true;
    return /[a-zA-Z]/.test(value);
  }
});

// ==================== PASSWORD VALIDATIONS ====================

/**
 * Validates that password contains at least one lowercase letter
 * @returns {function} Yup test function
 */
export const passwordLowercaseTest = () => ({
  name: 'password-lowercase',
  message: 'Password must contain at least one lowercase letter',
  test: function(value) {
    if (!value) return false;
    return /[a-z]/.test(value);
  }
});

/**
 * Validates that password contains at least one uppercase letter
 * @returns {function} Yup test function
 */
export const passwordUppercaseTest = () => ({
  name: 'password-uppercase',
  message: 'Password must contain at least one uppercase letter',
  test: function(value) {
    if (!value) return false;
    return /[A-Z]/.test(value);
  }
});

/**
 * Validates that password contains at least one number
 * @returns {function} Yup test function
 */
export const passwordNumberTest = () => ({
  name: 'password-number',
  message: 'Password must contain at least one number',
  test: function(value) {
    if (!value) return false;
    return /\d/.test(value);
  }
});

/**
 * Validates that password contains at least one special character
 * @returns {function} Yup test function
 */
export const passwordSpecialCharTest = () => ({
  name: 'password-special-char',
  message: 'Password must contain at least one special character (!@#$%^&*(),.?":{}|<>)',
  test: function(value) {
    if (!value) return false;
    return /[!@#$%^&*(),.?":{}|<>]/.test(value);
  }
});

/**
 * Validates that password doesn't contain only spaces
 * @returns {function} Yup test function
 */
export const passwordNoSpacesTest = () => ({
  name: 'password-no-spaces',
  message: 'Password cannot contain only spaces',
  test: function(value) {
    if (!value) return false;
    return value.trim().length > 0;
  }
});

// ==================== PHONE NUMBER VALIDATIONS ====================

/**
 * Validates phone number format for Indian mobile numbers
 * Validation regex for Indian mobile numbers (10 digits, starts with 6/7/8/9)
 * Supports optional +91 / 91 / 0 prefix
 * @returns {function} Yup test function
 */
export const phoneCharactersTest = () => ({
  name: 'phone-characters',
  message: 'Please enter a valid Indian mobile number (exactly 10 digits starting with 6, 7, 8, or 9)',
  test: function(value) {
    if (!value || value.trim() === '') return true;
    // Remove all spaces and special characters
    const cleaned = value.replace(/[\s\-\(\)]/g, '');
    // Validate exactly 10 digits
    return /^[6-9]\d{9}$/.test(cleaned);
  }
});

/**
 * Validates phone number length (max 13 characters including +91 prefix)
 * @returns {function} Yup test function
 */
export const phoneDigitCountTest = () => ({
  name: 'phone-digit-count',
  message: 'Phone number must be exactly 10 digits',
  test: function(value) {
    if (!value || value.trim() === '') return true;
    // Remove spaces and special characters for length check
    const cleaned = value.replace(/[\s\-\(\)]/g, '');
    // Exactly 10 digits
    return cleaned.length === 10;
  }
});

// ==================== COMPLETE VALIDATION SCHEMAS ====================

/**
 * Complete validation for first name (required)
 * @param {number} maxLength - Maximum length (default: 255)
 */
export const firstNameValidation = (maxLength = 255) => yup
  .string()
  .required("First Name is required")
  .max(maxLength, `First Name must not exceed ${maxLength} characters`)
  .test(noOnlySpacesTest(true))
  .test(noSpecialCharactersTest())
  .test(containsLetterTest());

/**
 * Complete validation for last name (optional)
 * @param {number} maxLength - Maximum length (default: 255)
 */
export const lastNameValidation = (maxLength = 255) => yup
  .string()
  .max(maxLength, `Last Name must not exceed ${maxLength} characters`)
  .test(noOnlySpacesTest(false))
  .test(noSpecialCharactersTest())
  .test(containsLetterTest());

/**
 * Complete validation for phone number (optional)
 */
export const phoneNumberValidation = () => yup
  .string()
  .nullable()
  .optional()
  .test(noOnlySpacesTest(false))
  .test(phoneCharactersTest())
  .test(phoneDigitCountTest());

/**
 * Complete validation for required phone number
 */
export const phoneNumberRequiredValidation = () => yup
  .string()
  .required("Phone number is required")
  .test(noOnlySpacesTest(true))
  .test(phoneCharactersTest())
  .test(phoneDigitCountTest());

/**
 * Validation for text fields that allow alphanumeric
 * (e.g., job titles, company names)
 * @param {string} fieldName - Name of the field
 * @param {boolean} isRequired - Whether the field is required
 * @param {number} maxLength - Maximum length (default: 255)
 */
export const textFieldValidation = (fieldName, isRequired = false, maxLength = 255) => {
  let schema = yup.string();
  
  if (isRequired) {
    schema = schema.required(`${fieldName} is required`);
  }
  
  return schema
    .max(maxLength, `${fieldName} must not exceed ${maxLength} characters`)
    .test(noOnlySpacesTest(isRequired));
};

/**
 * Validation for name fields (only letters and spaces)
 * @param {string} fieldName - Name of the field
 * @param {boolean} isRequired - Whether the field is required
 * @param {number} maxLength - Maximum length (default: 255)
 */
export const nameFieldValidation = (fieldName, isRequired = false, maxLength = 255) => {
  let schema = yup.string();
  
  if (isRequired) {
    schema = schema.required(`${fieldName} is required`);
  }
  
  return schema
    .max(maxLength, `${fieldName} must not exceed ${maxLength} characters`)
    .test(noOnlySpacesTest(isRequired))
    .test(noSpecialCharactersTest())
    .test(containsLetterTest());
};

/**
 * Complete validation for password (with all security rules)
 * @param {string} fieldName - Name of the field (e.g., "Password", "New Password")
 * @param {number} minLength - Minimum password length (default: 8)
 * @param {number} maxLength - Maximum password length (default: 64)
 * @returns {yup.StringSchema} Yup schema with all password validations
 */
export const passwordValidation = (fieldName = "Password", minLength = 8, maxLength = 64) => yup
  .string()
  .required(`${fieldName} is required`)
  .min(minLength, `${fieldName} must be at least ${minLength} characters`)
  .max(maxLength, `${fieldName} must not exceed ${maxLength} characters`)
  .test(passwordNoSpacesTest())
  .test(passwordLowercaseTest())
  .test(passwordUppercaseTest())
  .test(passwordNumberTest())
  .test(passwordSpecialCharTest());

/**
 * Complete validation for confirm password field
 * @param {string} passwordFieldName - Name of the password field to match against
 * @param {string} fieldLabel - Label for error messages
 * @returns {yup.StringSchema} Yup schema with confirmation validation
 */
export const confirmPasswordValidation = (passwordFieldName = "newPassword", fieldLabel = "Confirm Password") => yup
  .string()
  .required(`${fieldLabel} is required`)
  .oneOf([yup.ref(passwordFieldName), null], "Passwords must match");

// ==================== INPUT HANDLER UTILITIES ====================

/**
 * Clean text input by removing leading spaces and multiple consecutive spaces
 * @param {string} value - The input value
 * @returns {string} Cleaned value
 */
export const cleanTextInput = (value) => {
  if (!value) return '';
  return value.replace(/^\s+/, '').replace(/\s{2,}/g, ' ');
};

/**
 * Clean phone input by removing leading spaces and multiple consecutive spaces
 * Also validates allowed characters
 * @param {string} value - The input value
 * @returns {string} Cleaned value or empty string if invalid
 */
export const cleanPhoneInput = (value) => {
  if (!value) return '';
  const validChars = /^[\d\s\+\-\(\)]*$/;
  if (!validChars.test(value)) return value.slice(0, -1); // Remove last invalid char
  return value.replace(/^\s+/, '').replace(/\s{2,}/g, ' ');
};

/**
 * Trim value on blur
 * @param {string} value - The input value
 * @returns {string} Trimmed value
 */
export const trimOnBlur = (value) => {
  if (!value) return '';
  return value.trim();
};

/**
 * Create onChange handler for text inputs with validation
 * @param {function} setFieldValue - Formik setFieldValue function
 * @param {string} fieldName - Name of the field
 * @returns {function} onChange handler
 */
export const createTextInputHandler = (setFieldValue, fieldName) => (e) => {
  const cleaned = cleanTextInput(e.target.value);
  setFieldValue(fieldName, cleaned);
};

/**
 * Create onChange handler for phone inputs with validation
 * @param {function} setFieldValue - Formik setFieldValue function
 * @param {string} fieldName - Name of the field
 * @param {number} maxDigits - Maximum number of digits allowed (default: 10)
 * @returns {function} onChange handler
 */
export const createPhoneInputHandler = (setFieldValue, fieldName, maxDigits = 10) => (e) => {
  const input = e.target.value;
  const validChars = /^[\d\s\+\-\(\)]*$/;
  
  if (validChars.test(input)) {
    const digitsOnly = input.replace(/\D/g, '');
    if (digitsOnly.length <= maxDigits) {
      const cleaned = cleanPhoneInput(input);
      setFieldValue(fieldName, cleaned);
    }
  }
};

/**
 * Create onBlur handler with trim functionality
 * @param {function} handleBlur - Formik handleBlur function
 * @param {function} setFieldValue - Formik setFieldValue function
 * @param {string} fieldName - Name of the field
 * @returns {function} onBlur handler
 */
export const createTrimBlurHandler = (handleBlur, setFieldValue, fieldName) => (e) => {
  handleBlur(e);
  if (e.target.value) {
    setFieldValue(fieldName, trimOnBlur(e.target.value));
  }
};

/**
 * Create onKeyDown handler for phone inputs
 * Prevents invalid characters from being typed
 * @param {number} maxDigits - Maximum number of digits allowed (default: 16)
 * @returns {function} onKeyDown handler
 */
export const createPhoneKeyDownHandler = (maxDigits = 13) => (e) => {
  const allowedKeys = [
    "Backspace",
    "ArrowLeft",
    "ArrowRight",
    "Tab",
    "Delete",
  ];
  const allowedChars = /^[\d\+\-\(\)\s]$/;
  const isCopyPaste =
    (e.ctrlKey || e.metaKey) &&
    ["c", "v", "x", "a"].includes(e.key.toLowerCase());
  
  if (isCopyPaste || allowedKeys.includes(e.key)) return;

  // Get current value and check digit count
  const currentValue = e.currentTarget.value;
  const digitsOnly = currentValue.replace(/\D/g, "");

  // Prevent input if max digits reached
  if (digitsOnly.length >= maxDigits && /^\d$/.test(e.key)) {
    e.preventDefault();
    return;
  }

  // Allow only valid phone characters
  if (!allowedChars.test(e.key)) {
    e.preventDefault();
  }
};

